/**
 * Phase 22 / Task 22.1 — push-notification storage (migration 0100). Plan of record:
 * gsd/phases/phase_22_push_notifications.md §4; decisions DEC-225..DEC-237.
 *
 * - `push_devices`: one row per (installation, user, tenant) binding. At most ONE live
 *   row per physical device across all tenants (partial unique index), which is what
 *   stops a shared tablet delivering a previous driver's notifications (DEC-231).
 *   Registration calls the SECURITY DEFINER `push_rebind_installation()` first.
 * - `notification_preferences`: per-user opt-outs; absence = the event's default.
 * - `notification_deliveries`: the outbox the sender drains, and the delivery log.
 *
 * All three are tenant-scoped under RLS (USING + WITH CHECK on app.current_tenant_id).
 */
import {
  boolean,
  check,
  index,
  integer,
  jsonb,
  pgPolicy,
  pgTable,
  primaryKey,
  text,
  timestamp,
  unique,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { tenants, users } from './users.js';
import { appUserRole } from './enums.js';

const tenantIsolation = sql`tenant_id::text = current_setting('app.current_tenant_id', true)`;

export const PUSH_DEVICE_REVOKE_REASONS = [
  'LOGOUT',
  'DRIVER_SWITCH',
  'TENANT_SWITCH',
  'SHIFT_END',
  'REBOUND',
  'PROVIDER_UNREGISTERED',
] as const;
export type PushDeviceRevokeReason = (typeof PUSH_DEVICE_REVOKE_REASONS)[number];

export const pushDevices = pgTable(
  'push_devices',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    /** The SIGNED-IN user the binding delivers to; never "the device's owner". */
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    /** Random UUID generated once per app install, device-wide (survives logout). */
    installationId: uuid('installation_id').notNull(),
    platform: text('platform').$type<'android' | 'ios'>().notNull(),
    provider: text('provider').$type<'expo'>().notNull().default('expo'),
    /** Expo push token; NULL only when the OS permission is denied. Never log in full. */
    pushToken: text('push_token'),
    deviceMode: text('device_mode').$type<'PERSONAL' | 'SHARED'>().notNull(),
    permission: text('permission').$type<'GRANTED' | 'DENIED'>().notNull(),
    appVersion: text('app_version'),
    registeredAt: timestamp('registered_at', { withTimezone: true }).notNull().defaultNow(),
    lastSeenAt: timestamp('last_seen_at', { withTimezone: true }).notNull().defaultNow(),
    revokedAt: timestamp('revoked_at', { withTimezone: true }),
    revokeReason: text('revoke_reason').$type<PushDeviceRevokeReason>(),
  },
  (t) => [
    // Any ON CONFLICT against this index must repeat the predicate.
    uniqueIndex('push_devices_one_live_per_installation')
      .on(t.installationId)
      .where(sql`${t.revokedAt} IS NULL`),
    index('push_devices_live_user_idx')
      .on(t.tenantId, t.userId)
      .where(sql`${t.revokedAt} IS NULL`),
    check('push_devices_platform_check', sql`${t.platform} IN ('android', 'ios')`),
    check('push_devices_provider_check', sql`${t.provider} IN ('expo')`),
    check('push_devices_device_mode_check', sql`${t.deviceMode} IN ('PERSONAL', 'SHARED')`),
    check('push_devices_permission_check', sql`${t.permission} IN ('GRANTED', 'DENIED')`),
    check(
      'push_devices_token_when_granted',
      sql`${t.permission} = 'DENIED' OR ${t.pushToken} IS NOT NULL`,
    ),
    check('push_devices_revoke_pair', sql`(${t.revokedAt} IS NULL) = (${t.revokeReason} IS NULL)`),
    pgPolicy('push_devices_tenant_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: tenantIsolation,
      withCheck: tenantIsolation,
    }),
  ],
);

export type PushDeviceRow = typeof pushDevices.$inferSelect;
export type NewPushDeviceRow = typeof pushDevices.$inferInsert;

export const notificationPreferences = pgTable(
  'notification_preferences',
  {
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    eventType: text('event_type').notNull(),
    /** v1 is push only (DEC-226 / DEC-227). */
    channel: text('channel').$type<'PUSH'>().notNull(),
    enabled: boolean('enabled').notNull(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    primaryKey({
      name: 'notification_preferences_pkey',
      columns: [t.tenantId, t.userId, t.eventType, t.channel],
    }),
    check('notification_preferences_event_type_shape', sql`${t.eventType} ~ '^[A-Z][A-Z0-9_]*$'`),
    check('notification_preferences_channel_check', sql`${t.channel} IN ('PUSH')`),
    pgPolicy('notification_preferences_tenant_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: tenantIsolation,
      withCheck: tenantIsolation,
    }),
  ],
);

export type NotificationPreferenceRow = typeof notificationPreferences.$inferSelect;

export type NotificationDeliveryStatus = 'QUEUED' | 'SENT' | 'DELIVERED' | 'FAILED' | 'SUPPRESSED';
export type NotificationSuppressReason =
  | 'NO_DEVICE'
  | 'PREF_OFF'
  | 'PERMISSION_DENIED'
  | 'SENDING_DISABLED';

export const notificationDeliveries = pgTable(
  'notification_deliveries',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    eventType: text('event_type').notNull(),
    recipientUserId: uuid('recipient_user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    pushDeviceId: uuid('push_device_id').references(() => pushDevices.id, {
      onDelete: 'set null',
    }),
    channel: text('channel').$type<'PUSH'>().notNull().default('PUSH'),
    /** Idempotent enqueue, e.g. `JOB_CANCELLED:<jobId>:<userId>`. Unique per tenant. */
    dedupeKey: text('dedupe_key').notNull(),
    status: text('status').$type<NotificationDeliveryStatus>().notNull().default('QUEUED'),
    suppressReason: text('suppress_reason').$type<NotificationSuppressReason>(),
    /** Exactly what was shown, already redacted by the payload builder (DEC-229). */
    title: text('title').notNull(),
    body: text('body').notNull(),
    /** Routing ids only. */
    data: jsonb('data').$type<Record<string, string>>().notNull().default({}),
    providerTicketId: text('provider_ticket_id'),
    providerError: text('provider_error'),
    attempts: integer('attempts').notNull().default(0),
    nextAttemptAt: timestamp('next_attempt_at', { withTimezone: true }).notNull().defaultNow(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    sentAt: timestamp('sent_at', { withTimezone: true }),
    receiptCheckedAt: timestamp('receipt_checked_at', { withTimezone: true }),
  },
  (t) => [
    unique('notification_deliveries_dedupe_uq').on(t.tenantId, t.dedupeKey),
    index('notification_deliveries_pending_idx')
      .on(t.tenantId, t.status, t.nextAttemptAt)
      .where(sql`${t.status} IN ('QUEUED', 'SENT')`),
    index('notification_deliveries_created_idx').on(t.createdAt),
    check('notification_deliveries_event_type_shape', sql`${t.eventType} ~ '^[A-Z][A-Z0-9_]*$'`),
    check('notification_deliveries_channel_check', sql`${t.channel} IN ('PUSH')`),
    check(
      'notification_deliveries_status_check',
      sql`${t.status} IN ('QUEUED', 'SENT', 'DELIVERED', 'FAILED', 'SUPPRESSED')`,
    ),
    check(
      'notification_deliveries_suppress_reason_check',
      sql`${t.suppressReason} IS NULL OR ${t.suppressReason} IN ('NO_DEVICE', 'PREF_OFF', 'PERMISSION_DENIED', 'SENDING_DISABLED')`,
    ),
    check(
      'notification_deliveries_suppress_pair',
      sql`(${t.status} = 'SUPPRESSED') = (${t.suppressReason} IS NOT NULL)`,
    ),
    check('notification_deliveries_attempts_nonneg', sql`${t.attempts} >= 0`),
    pgPolicy('notification_deliveries_tenant_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: tenantIsolation,
      withCheck: tenantIsolation,
    }),
  ],
);

export type NotificationDeliveryRow = typeof notificationDeliveries.$inferSelect;
export type NewNotificationDeliveryRow = typeof notificationDeliveries.$inferInsert;
